import type { CreativeType } from '@/editor/shared/constants/creatives-types.enum';
import EmptyImageCard from '@/shared/components/platform-previews/previews/EmptyImageCard';
import type { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import type { FC} from 'react';
import { memo, useState, type JSX } from 'react';
import {
  useCreateAndUploadEmeraMediaForMetaMutation,
  useLazyCreateImageOverlayQuery,
} from '@/editor/services/http/media-library.api';
import ImageBox from './ImageBox';
import useCheckIfSelectedImage from './hooks/useCheckIfSelectedImage';
import ConfirmImageDialog from './ConfirmImageDialog';
import useHandleImageClick from './hooks/useHandleImageClick';
import type { IMediaLibraryDrawerState } from '@/editor/store/reducers/modals-drawers.slice';
import type { ICreativeFormsStatePerPlatform, ICreativeStepState } from '@/editor/store/reducers/creative-step.slice';
import type { IBasicCreativeFormValues } from '@/editor/shared/components/forms/creatives/models/basic-creative-form-values.model';
import { creativeFormService } from '@/editor/pages/create-ads-steps-page/components/creatives-step/services';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';

export interface IMediaLibraryImageItemProps {
  item: IMediaLibraryImageModel;
  queue: number;
  drawerState: IMediaLibraryDrawerState;
  creativeState: ICreativeStepState;
}

const MediaLibraryImageItem: FC<IMediaLibraryImageItemProps> = memo(
  ({ item, queue, drawerState, creativeState }): JSX.Element => {
    const [imageDialogOpen, setDialogOpen] = useState<boolean>(false);
    const [imageOverlayUrl, setImageOverlayUrl] = useState<string | undefined>('');
    const [originalImage, setOriginalImage] = useState<IMediaLibraryImageModel | null>(null);
    const [aftermarketingFormData, setAftermarketingFormData] = useState<Partial<IBasicCreativeFormValues>>();

    const [createImageOverlay, { isFetching: overlayFetching }] = useLazyCreateImageOverlayQuery();
    const [createImageForMetaEmera, { isLoading: metaEmeraFetching }] = useCreateAndUploadEmeraMediaForMetaMutation();
    const state = creativeState.creativeFormsState;
    const data = state[AvailablePlatformsEnum.META][drawerState.creativeType as CreativeType];

    const { isImageSelectedByPlatform, isImageSomeWhereSelected } = useCheckIfSelectedImage(
      drawerState,
      creativeState,
      item
    );
    const { handleImageClick } = useHandleImageClick(isImageSelectedByPlatform, drawerState, creativeState);

    const handleImageOverlay = (image: IMediaLibraryImageModel) => {
      setDialogOpen(true);
      createImageOverlay({ propertyFileId: image.id }).then((res) => {
        setOriginalImage(image);
        setImageOverlayUrl(res.data?.imageUrl);
        setAftermarketingFormData(res.data?.data);
      });
    };
    const handleImageForMetaEmera = (image: IMediaLibraryImageModel) => {
      setDialogOpen(true);
      createImageForMetaEmera({
        propertyFileId: image.id,
        payload: {
          withSave: false,
          isMobile: false,
          localAreaName: data?.localAreaName!,
          propertyInfo: data?.propertyInfo!,
          desc: data?.description!,
          brokerName: data?.employeeName!,
          brokerTitle: data?.employeeTitle!,
        },
      }).then((res) => {
        setOriginalImage(image);
        setImageOverlayUrl(res.data?.imageUrl);
      });
    };

    const onDialogClose = (confirm: boolean): void => {
      if (confirm) {
        creativeFormService.setFormValues(
          aftermarketingFormData!,
          drawerState.platform!,
          drawerState.creativeType as keyof ICreativeFormsStatePerPlatform
        );
      }

      setDialogOpen(false);
    };

    return (
      <>
        <ImageListItem sx={{ border: isImageSomeWhereSelected() ? '5px solid #000' : '5px solid transparent' }}>
          {item.preSignedUrl ? (
            <ImageBox
              image={item}
              platform={drawerState.platform!}
              creativeType={drawerState.creativeType! as CreativeType}
              handleImageClick={handleImageClick}
              handleImageForMetaEmera={handleImageForMetaEmera}
              handleImageOverlay={handleImageOverlay}
              isImageSelectedByPlatform={isImageSelectedByPlatform}
              isImageSomeWhereSelected={isImageSomeWhereSelected}
            />
          ) : (
            <EmptyImageCard />
          )}
          <ImageListItemBar
            sx={{
              display: { xs: 'none', md: 'inline' },
              p: 0,
              '& .MuiImageListItemBar-titleWrap': {
                padding: '4px 8px',
              },
            }}
            title={queue + 1 + ' - ' + item.width + 'x' + item.height}></ImageListItemBar>
        </ImageListItem>
        <ConfirmImageDialog
          originalImage={originalImage}
          open={imageDialogOpen}
          imageIsLoading={overlayFetching || metaEmeraFetching}
          imageUrl={imageOverlayUrl as string}
          onClose={onDialogClose}
          item={item}
        />
      </>
    );
  }
);

export default MediaLibraryImageItem;
